import Step1Image from "@/public/icons/hiw-step1.svg";
import Step2Image from "@/public/icons/hiw-step2.svg";
import Step3Image from "@/public/icons/hiw-step3.svg";
import Step4Image from "@/public/icons/hiw-step4.svg";
import Step5Image from "@/public/icons/hiw-step5.svg";
import routes from "@/constants/routes";
import { FaqItem } from "./FaqAccordion";

export type StepCta =
  | { kind: "link"; label: string; href: string }
  | { kind: "scroll"; label: string; scrollTo: string };

export type StepData = {
  n: number;
  title: string;
  time: string;
  lead: string;
  body: string;
  Icon: typeof Step1Image;
  cta: StepCta;
  faqs: FaqItem[];
};

export const STEPS: StepData[] = [
  {
    n: 1,
    title: "Get your budget",
    time: "10 seconds",
    Icon: Step1Image,
    lead: "Use our calculator to see what you could afford.",
    body: "It takes 10 seconds. Enter your annual income — including anyone moving in with you — and we'll tell you your maximum monthly rent and your home buy-back price.",
    cta: { kind: "scroll", label: "Get your budget", scrollTo: "calculator" },
    faqs: [
      {
        question: "How do you decide my budget?",
        answer:
          "It's based on your income and the income of any fellow applicants. We make sure your monthly maximum is never higher than you could comfortably afford.",
      },
      {
        question: "What's the basic eligibility criteria?",
        answer:
          "You need to be over 18 and have the right to live in the UK. We also run an industry-standard check using secure, modern technology: <ul><li>Verifying income and outgoings via Open Banking</li><li>Checking for fraud and bankruptcy via credit bureaus</li><li>Reference checks with landlords and employers</li></ul>",
      },
      {
        question: "How can Keyzy give back all my rent and still make money?",
        answer:
          "We're a rent-to-own company — we exist to sell homes to first-time buyers, not to be long-term landlords. We make money by selling the home to you at the end of your tenancy, and we work with investors and developers to make new-builds available. A one-off future buy-back fee applies at the start: £499 for one applicant, £999 for two, or £1,499 for three or more.",
      },
    ],
  },
  {
    n: 2,
    title: "Find your ideal home",
    time: "1 week",
    Icon: Step2Image,
    lead: "The hunt is on — browse our listings and find a home that suits you.",
    body: "Find one you like that's within budget? Complete a short form to get started. If nothing fits yet, register your interest for homes in your area. If you qualify we'll set up a viewing, then you (and any fellow applicant) complete a full application.",
    cta: { kind: "link", label: "Browse rent-to-own homes", href: routes.searchHomes },
    faqs: [
      {
        question: "Where does Keyzy source homes?",
        answer:
          "Keyzy is currently available for homes in England & Wales, and we're hoping to expand across the rest of the UK soon. We find the homes for our solution; you then rent them to buy back later.",
      },
      {
        question: "How long between applying and moving in?",
        answer:
          "Every property has a date it's available from. Once you've gone through approval and the rental is affordable, you can move in from that date. It can be a fast process, so it's good to be ready to move when the home is available.",
      },
    ],
  },
  {
    n: 3,
    title: "Get approved for Keyzy",
    time: "2–3 days",
    Icon: Step3Image,
    lead: "Use our quick, easy and secure online portal.",
    body: "We do a detailed review of you and any fellow applicants. You'll submit your ID, proof of income and bank statements through the secure portal. Our team reviews everything and confirms once you've passed all our checks — then you can arrange moving in.",
    cta: { kind: "link", label: "Apply now", href: routes.register },
    faqs: [
      {
        question: "Do I need a perfect credit score?",
        answer:
          "No. But we do take your income and spending over the past year into account to make sure the monthly payments are affordable. We can't accept applicants with an IVA or bankruptcy in the last 3 years, missed rent or mortgage payments in the last 12 months, or Defaults and CCJs over £250 in the last 12 months. Once you're a Keyzy homeowner we report your rental payments to Equifax, which can improve your credit position.",
      },
      {
        question: "How many people can apply with me?",
        answer: "We accept up to 3 applicants.",
      },
    ],
  },
  {
    n: 4,
    title: "Move in and start saving",
    time: "1 day",
    Icon: Step4Image,
    lead: "Treat the home like your own and start working towards owning it.",
    body: "Time to move in. You'll sign your rental and future buy-back agreements, pay your first month's rent, and the keys are yours. Your monthly payments are locked — no increases — and your future buy-back price is set ahead of time, so you can focus on building your deposit and getting mortgage-ready.",
    cta: { kind: "link", label: "More FAQs", href: routes.faqs },
    faqs: [
      {
        question: "Who's responsible for maintenance?",
        answer:
          "Keyzy or the owner covers insurable costs like storm damage or burst pipes, and we make sure your gas and electrical safety certificates are in place. You're responsible for wear and tear and the day-to-day upkeep of the home.",
      },
      {
        question: "How long is my Keyzy agreement?",
        answer:
          "Your rental agreement — an assured periodic tenancy — is a rolling monthly agreement with no end date. Your future buy-back agreement is valid for 24 months from the day your rental begins.",
      },
    ],
  },
  {
    n: 5,
    title: "Buy the home when you're ready",
    time: "2 years",
    Icon: Step5Image,
    lead: "When it's time to buy — or if you decide to walk away — we're with you every step.",
    body: "Buy the home from Keyzy using the rent you've already paid as your deposit. And if you decide not to buy, you can walk away with no penalties, just like any other rental you choose to leave.",
    cta: { kind: "scroll", label: "Calculate your budget", scrollTo: "calculator" },
    faqs: [
      {
        question: "How do you decide the future buy-back price?",
        answer:
          "The future buy-back price is agreed in advance and reflects the current market value with an estimated increase included.",
      },
      {
        question: "What if I can't get a mortgage?",
        answer:
          "You're free to leave with no penalties. We could also look at extending your future buy-back agreement so you can keep getting mortgage-ready.",
      },
    ],
  },
];
